import { notification } from 'antd';
import axios from 'axios';
import { normalize } from 'normalizr';

import { ACTION_TYPES as DASHBOARD_ACTION_TYPES } from './constants.js';
import schemas from './schemas.js';

const baseUrl = process.env.REACT_APP_API_URL?.replace('/api/v1', '/api/v2');

const config = {
  headers: { Authorization: '' },
};

export const createDashboard = (intl, dashboardData, callBack = () => {}, { silent = false } = {}) => {
  const fn = '[createDashboard]';
  return async (dispatch) => {
    dispatch({ type: DASHBOARD_ACTION_TYPES.CREATE_DASHBOARD_REQUEST });
    try {
      config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
      const res = await axios.post(
        `${baseUrl}/dashboard/create`,
        dashboardData,
        config,
      );

      dispatch({
        type: DASHBOARD_ACTION_TYPES.CREATE_DASHBOARD_SUCCESS,
        payload: res?.data?.data,
      });

      if (!silent) {
        notification.success({
          message: intl.formatMessage({
            id: 'DASHBOARD_CREATED',
            defaultMessage: 'Dashboard created successfully',
          }),
          placement: 'bottomRight',
        });
      }

      callBack(res?.data?.data);
    } catch (error) {
      dispatch({
        type: DASHBOARD_ACTION_TYPES.CREATE_DASHBOARD_FAILURE,
        payload: error?.response?.data?.message || error.message,
      });
      console.error(
        `${fn}: failed to create the dashboard`,
        error?.response?.data || error.message,
      );
      notification.error({
        message: intl.formatMessage({
          id: 'DASHBOARD_CREATION_FAILURE',
          defaultMessage:
            'Failed to create the dashboard. Please try again later.',
        }),
        placement: 'bottomRight',
      });
    }
  };
};

export const getDashboard = (intl, dashboardId, skipLoading) => {
  const fn = '[getDashboard]';
  return async (dispatch) => {
    if (!skipLoading) {
      dispatch({
        type: DASHBOARD_ACTION_TYPES.GET_DASHBOARD_REQUEST,
        payload: { dashboardId },
      });
    }
    try {
      config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
      const res = await axios.get(
        `${baseUrl}/dashboard/${dashboardId}`,
        config,
      );
      const normalizedResult = normalize(
        res?.data,
        schemas.GET_DASHBOARD_SCHEMA,
      );
      dispatch({
        type: DASHBOARD_ACTION_TYPES.GET_DASHBOARD_SUCCESS,
        payload: normalizedResult,
      });
      return true;
    } catch (error) {
      const status = error?.response?.status;
      const reason = error?.response?.data?.reason;
      dispatch({
        type: DASHBOARD_ACTION_TYPES.GET_DASHBOARD_FAILURE,
        payload: {
          dashboardId,
          error: error?.response?.data?.message || error.message,
        },
      });
      console.error(
        `${fn}: failed to fetch dashboard`,
        error?.response?.data || error.message,
      );
      // Return a structured result so the container can show the right error screen
      // without showing a generic toast for expected access/not-found cases
      if (status === 404 || reason === 'NOT_FOUND') {
        return { ok: false, reason: 'NOT_FOUND' };
      }
      if (status === 403 || reason === 'ACCESS_DENIED') {
        return { ok: false, reason: 'ACCESS_DENIED' };
      }
      notification.error({
        message: intl.formatMessage({
          id: 'DASHBOARD_FETCH_FAILURE',
          defaultMessage: 'Failed to fetch the dashboard. Please try again later.',
        }),
        placement: 'bottomRight',
      });
      return { ok: false, reason: 'ERROR' };
    }
  };
};

export const getDashboardsByWorkspace = (intl, wId) => {
  const fn = '[getDashboardsByWorkspace]';
  const workspaceId = wId || localStorage.getItem('workspaceId');

  return async (dispatch) => {
    dispatch({
      type: DASHBOARD_ACTION_TYPES.GET_DASHBOARDS_BY_WORKSPACE_REQUEST,
    });
    try {
      config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
      const res = await axios.get(
        `${baseUrl}/dashboard/workspace/${workspaceId}`,
        config,
      );
      dispatch({
        type: DASHBOARD_ACTION_TYPES.GET_DASHBOARDS_BY_WORKSPACE_SUCCESS,
        payload: res?.data?.data,
      });
      return res?.data?.data || [];
    } catch (error) {
      dispatch({
        type: DASHBOARD_ACTION_TYPES.GET_DASHBOARDS_BY_WORKSPACE_FAILURE,
        payload: error?.response?.data?.message || error.message,
      });
      console.error(
        `${fn}: failed to get dashboards`,
        error?.response?.data || error.message,
      );
      notification.error({
        message: intl.formatMessage({
          id: 'FAILED_TO_GET_DASHBOARDS',
          defaultMessage: 'Failed to get dashboards. Please try again later.',
        }),
        placement: 'bottomRight',
      });
    }
  };
};

export const updateDashboard = (intl, dashboardId, title, extraFields = {}, { silent = false } = {}) => {
  const fn = '[updateDashboard]';
  return async (dispatch) => {
    dispatch({
      type: DASHBOARD_ACTION_TYPES.UPDATE_DASHBOARD_REQUEST,
      payload: { dashboardId },
    });
    try {
      config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
      const body = { ...(title !== null && title !== undefined ? { title } : {}), ...extraFields };
      const res = await axios.put(
        `${baseUrl}/dashboard/${dashboardId}`,
        body,
        config,
      );
      dispatch({
        type: DASHBOARD_ACTION_TYPES.UPDATE_DASHBOARD_SUCCESS,
        payload: res?.data?.data,
      });

      if (!silent) {
        notification.success({
          message: intl.formatMessage({
            id: 'DASHBOARD_UPDATE_SUCCESS',
            defaultMessage: 'The dashboard was successfully updated.',
          }),
          placement: 'bottomRight',
        });
      }
      return true;
    } catch (error) {
      dispatch({
        type: DASHBOARD_ACTION_TYPES.UPDATE_DASHBOARD_FAILURE,
        payload: error?.response?.data?.message || error.message,
      });
      console.error(
        `${fn}: failed to update the dashboard`,
        error?.response?.data || error.message,
      );
      notification.error({
        message: intl.formatMessage({
          id: 'DASHBOARD_UPDATE_FAILURE',
          defaultMessage:
            'Failed to update the dashboard. Please try again later.',
        }),
        placement: 'bottomRight',
      });
    }
  };
};

export const getDashboardUsers = (dashboardId) => async () => {
  try {
    config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
    const res = await axios.get(`${baseUrl}/dashboard/${dashboardId}/users`, config);
    return res?.data?.data || [];
  } catch (error) {
    console.error('[getDashboardUsers]: failed', error?.response?.data || error.message);
    return [];
  }
};

export const updateDashboardUsers = (dashboardId, users, operation) => async () => {
  try {
    config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
    await axios.post(`${baseUrl}/dashboard/${dashboardId}/users/${operation}`, { users }, config);
  } catch (error) {
    console.error('[updateDashboardUsers]: failed', error?.response?.data || error.message);
    throw error;
  }
};

export const deleteDashboard = (intl, dashboardId) => {
  const fn = '[deleteDashboard]';
  return async (dispatch) => {
    dispatch({
      type: DASHBOARD_ACTION_TYPES.DELETE_DASHBOARD_REQUEST,
      payload: { dashboardId },
    });
    try {
      config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
      await axios.delete(`${baseUrl}/dashboard/${dashboardId}`, config);
      dispatch({
        type: DASHBOARD_ACTION_TYPES.DELETE_DASHBOARD_SUCCESS,
        payload: { dashboardId },
      });

      notification.success({
        message: intl.formatMessage({
          id: 'DASHBOARD_DELETION_SUCCESS',
          defaultMessage: 'The dashboard was successfully deleted.',
        }),
        placement: 'bottomRight',
      });
    } catch (error) {
      dispatch({
        type: DASHBOARD_ACTION_TYPES.DELETE_DASHBOARD_FAILURE,
        payload: error?.response?.data?.message || error.message,
      });
      console.error(
        `${fn}: failed to delete the dashboard`,
        error?.response?.data || error.message,
      );
      notification.error({
        message: intl.formatMessage({
          id: 'DASHBOARD_DELETION_FAILURE',
          defaultMessage:
            'Failed to delete the dashboard. Please try again later.',
        }),
        placement: 'bottomRight',
      });
    }
  };
};
